'use strict';

const WARRANTY_DATA = {
  500: { "2": 51.99, "3": 75.99, "5": 139.99 },
  1500: { "2": 75.99, "3": 115.99, "5": 163.99 },
  2500: { "2": 83.99, "3": 131.99, "5": 195.99 },
  3500: { "2": 91.99, "3": 151.99, "5": 239.99 },
  5000: { "2": 155.99, "3": 235.99, "5": 359.99 },
  10000: { "2": 215.99, "3": 335.99, "5": 489.99 },
  15000: { "2": 275.99, "3": 459.99, "5": 759.99 },
  20000: { "2": 355.99, "3": 659.99, "5": 959.99 }
};

function getWarrantyTier(priceCents) {
  const price = Number(priceCents) / 100;
  const tiers = Object.keys(WARRANTY_DATA).map(Number).sort((a, b) => a - b);

  if (!Number.isFinite(price) || price < 0) return tiers[0];

  // The $500 tier is the default for prices below $500.
  // At each threshold, use the highest tier whose starting price is <= item price.
  let selectedTier = tiers[0];
  for (const tier of tiers) {
    if (price >= tier) selectedTier = tier;
    else break;
  }
  return selectedTier;
}

function getWarrantyPrices(priceCents) {
  return WARRANTY_DATA[getWarrantyTier(priceCents)];
}

const CPS_WARRANTY_VARIANTS = {
  500: {
    "2": { sku: "PLAT2500", variantId: 53155784458605 },
    "3": { sku: "PLAT3500", variantId: 53155790618989 },
    "5": { sku: "PLAT5500", variantId: 53155802415469 }
  },
  1500: {
    "2": { sku: "PLAT21500", variantId: 53155784556909 },
    "3": { sku: "PLAT31500", variantId: 53155791241581 },
    "5": { sku: "PLAT51500", variantId: 53155803857261 }
  },
  2500: {
    "2": { sku: "PLAT22500", variantId: 53155785834861 },
    "3": { sku: "PLAT32500", variantId: 53155791765869 },
    "5": { sku: "PLAT52500", variantId: 53155804512621 }
  },
  3500: {
    "2": { sku: "PLAT23500", variantId: 53155786490221 },
    "3": { sku: "PLAT33500", variantId: 53155792290157 },
    "5": { sku: "PLAT53500", variantId: 53155805331821 }
  },
  5000: {
    "2": { sku: "PLAT25000", variantId: 53155787571565 },
    "3": { sku: "PLAT35000", variantId: 53155792781677 },
    "5": { sku: "PLAT55000", variantId: 53155805987181 }
  },
  10000: {
    "2": { sku: "PLAT210000", variantId: 53155788357997 },
    "3": { sku: "PLAT310000", variantId: 53155794092397 },
    "5": { sku: "PLAT510000", variantId: 53155806708077 }
  },
  15000: {
    "2": { sku: "PLAT215000", variantId: 53155788980589 },
    "3": { sku: "PLAT315000", variantId: 53155794944365 },
    "5": { sku: "PLAT515000", variantId: 53155808215405 }
  },
  20000: {
    "2": { sku: "PLAT220000", variantId: 53155789832557 },
    "3": { sku: "PLAT320000", variantId: 53155795403117 },
    "5": { sku: "PLAT520000", variantId: 53155808903533 }
  }
};

function getCpsWarrantyVariant(priceCents, years) {
  const term = String(years);
  if (!["2", "3", "5"].includes(term)) return null;
  const tier = getWarrantyTier(priceCents);
  return CPS_WARRANTY_VARIANTS[tier]?.[term] || null;
}

function getWarrantyPrice(item) {
  const warranties = getWarrantyPrices(item.priceCents);
  const key = item.warranty === '2' ? '2' : item.warranty === '3' ? '3' : item.warranty === '5' ? '5' : null;
  return key ? warranties[key] : 0;
}

class BundleState {
  constructor() {
    this._data = new Map();
    this._listeners = [];
  }
  add(key, value) {
    this._data.set(key, { ...value, key });
    this._notify();
  }
  remove(key) {
    this._data.delete(key);
    this._notify();
  }
  has(key) { return this._data.has(key); }
  get(key) { return this._data.get(key) || null; }
  getAll() { return Array.from(this._data.values()); }
  get size() { return this._data.size; }
  onChange(cb) { this._listeners.push(cb); }
  clear() {
    this._data.clear();
    this._notify();
  }
  _notify() { this._listeners.forEach(cb => cb(this)); }
}

class PanelFilters {
  constructor(panelEl, bodyEl) {
    this._panel = panelEl;
    this._body = bodyEl || panelEl;
    this._active = {};
    const existing = this._body.querySelector(':scope > .bb__no-results');
    if (existing) { this._msg = existing; } else {
      this._msg = document.createElement('p');
      this._msg.className = 'bb__no-results';
      this._msg.textContent = 'No products match your selection.';
      this._msg.hidden = true;
      this._body.appendChild(this._msg);
    }
    this._bound = false;
    this._bindClick();
  }
  showAll() {
    this.reset(false);
    this._body.querySelectorAll('.bb__product').forEach(p => {
      p.classList.add('bb__product--visible');
      p.style.display = 'grid';
    });
    this._msg.hidden = true;
  }
  reset(applyProducts = true) {
    this._active = {};
    this._panel.querySelectorAll('.bb__filter-btn').forEach(b => {
      b.setAttribute('aria-pressed', 'false');
      b.classList.remove('is-active');
    });
    if (applyProducts) {
      this._msg.hidden = true;
      this._body.querySelectorAll('.bb__product').forEach(p => {
        p.classList.add('bb__product--visible');
        p.style.display = 'grid';
      });
    }
  }
  _bindClick() {
    if (this._bound) return;
    this._bound = true;
    this._handler = (e) => {
      const btn = e.target.closest('.bb__filter-btn');
      if (!btn || !this._panel.contains(btn)) return;
      const { filter, value } = btn.dataset;
      const norm = (value || '').toLowerCase();

      if (btn.getAttribute('aria-pressed') === 'true') {
        btn.setAttribute('aria-pressed', 'false');
        btn.classList.remove('is-active');
        delete this._active[filter];
        const group = btn.closest('.bb__filter-group');
        const selectedEl = group?.querySelector('.bb__filter-selected');
        if (selectedEl) selectedEl.textContent = '';
      } else {
        this._panel.querySelectorAll(`.bb__filter-btn[data-filter="${filter}"]`).forEach(b => {
          b.setAttribute('aria-pressed', 'false');
          b.classList.remove('is-active');
        });
        btn.setAttribute('aria-pressed', 'true');
        btn.classList.add('is-active');
        this._active[filter] = norm;
        const group = btn.closest('.bb__filter-group');
        const selectedEl = group?.querySelector('.bb__filter-selected');
        if (selectedEl) selectedEl.textContent = value.charAt(0).toUpperCase() + value.slice(1);
      }
      this._apply();
    };
    this._panel.addEventListener('click', this._handler);
  }
  _apply() {
    const activeFilters = Object.keys(this._active);
    let visible = 0;
    if (activeFilters.length === 0) {
      this._body.querySelectorAll('.bb__product').forEach(p => {
        p.classList.add('bb__product--visible');
        p.style.display = 'grid';
        visible++;
      });
    } else {
      this._body.querySelectorAll('.bb__product').forEach(p => {
        let match = true;
        for (const filter of activeFilters) {
          const productValue = (p.dataset[filter] || '').toLowerCase().trim();
          const selectedValue = (this._active[filter] || '').toLowerCase().trim();
          if (productValue !== selectedValue) { match = false; break; }
        }
        p.classList.toggle('bb__product--visible', match);
        p.style.display = match ? 'grid' : 'none';
        if (match) visible++;
      });
    }
    this._msg.hidden = visible > 0;
  }
  destroy() {
    if (this._handler) { this._panel.removeEventListener('click', this._handler); }
    this._bound = false;
    this._handler = null;
  }
}

const _esc = str => String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const _cap = str => str.charAt(0).toUpperCase() + str.slice(1);
const _warrantyHTML = (variantId, priceCents) => {
  const warranties = getWarrantyPrices(priceCents);
  let html = `<div class="bb__product-warranty-wrap"><h4 class="bb__warranty-mini-title">Extended Warranty</h4><div class="bb__product-warranty"><label><input type="radio" name="warranty-${variantId}" value="none" checked> No Warranty</label>`;
  for (const [years, price] of Object.entries(warranties)) {
    html += `<label><input type="radio" name="warranty-${variantId}" value="${years}"> ${years} Year (+$${price.toFixed(2)})</label>`;
  }
  html += `</div></div>`;
  return html;
};

class BundleExtras {
  constructor(panel, state, ui, data) {
    this._panel = panel;
    this._state = state;
    this._ui = ui;
    this._data = data || {};
    this._filter = null;
    this._current = null;
    this._filtersEl = panel.querySelector('.bb__extras-filters');
    this._productsEl = panel.querySelector('.bb__extras-products');
    this._bodyEl = panel.querySelector('.bb__extras-body');
    this._placeholder = panel.querySelector('.bb__extras-placeholder');
    this._bindCats();
  }

  _bindCats() {
    this._panel.querySelectorAll('.bb__extras-cat-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        this._panel.querySelectorAll('.bb__extras-cat-btn').forEach(b => {
          b.setAttribute('aria-pressed', 'false');
          b.classList.remove('is-active');
        });
        btn.setAttribute('aria-pressed', 'true');
        btn.classList.add('is-active');
        this._renderCategory(btn.dataset.handle);
      });
    });
  }

  _renderCategory(handle) {
    const products = this._data[handle];
    if (!products || !products.length) return;

    this._current = handle;
    this._filtersEl.innerHTML = '';
    this._productsEl.innerHTML = '';
    const sets = { size: new Set(), base: new Set(), accent: new Set(), fuel: new Set() };
    
    products.forEach(p => {
      if (p.size) sets.size.add(p.size.toLowerCase());
      if (p.base) sets.base.add(p.base.toLowerCase());
      if (p.accent) sets.accent.add(p.accent.toLowerCase());
      if (p.fuel) sets.fuel.add(p.fuel.toLowerCase());
    });

    [
      { key: 'size', label: 'Size:' },
      { key: 'base', label: 'Base Color:' },
      { key: 'accent', label: 'Accent Color:' },
    ].forEach(({ key, label }) => {
      if (!sets[key].size) return;
      const group = document.createElement('div');
      group.className = 'bb__filter-group';
      group.innerHTML = `<div class="bb__filter-head"><span class="bb__filter-label">${label}<strong class="bb__filter-selected"></strong></span></div><div class="bb__filter-btns"></div>`;
      const btnsEl = group.querySelector('.bb__filter-btns');

      [...sets[key]].sort().forEach(v => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = key === 'base' || key === 'accent' ? 'bb__filter-btn bb__filter-btn--swatch' : 'bb__filter-btn';
        if (key === 'base' || key === 'accent') {
          b.dataset.color = v;
          b.title = v;
          b.innerHTML = `<span class="visually-hidden">${_cap(v)}</span>`;
        } else {
          b.textContent = key === 'size' ? `${v}"` : _cap(v);
        }
        b.dataset.filter = key;
        b.dataset.value = v;
        b.setAttribute('aria-pressed', 'false');
        btnsEl.appendChild(b);
      });
      this._filtersEl.appendChild(group);
    });

    products.forEach(p => {
      const div = document.createElement('div');
      const isAvailable = p.available !== false; // Assume true if missing

      div.className = `bb__product bb__product--visible${!isAvailable ? ' bb__product--sold-out' : ''}`;
      div.dataset.size = (p.size || '').toLowerCase();
      div.dataset.base = (p.base || '').toLowerCase();
      div.dataset.accent = (p.accent || '').toLowerCase();
      div.dataset.fuel = (p.fuel || '').toLowerCase();
      div.dataset.variantId = p.variantId;
      div.dataset.title = p.title;
      div.dataset.description = p.description || p.title || '';
      div.dataset.tech = p.tech || '';
      div.dataset.dim = p.dim || '';
      div.dataset.cut = p.cut || '';
      div.dataset.pdf = p.pdf || '';
      div.dataset.sku = p.sku;
      div.dataset.priceCents = p.priceCents;
      div.dataset.price = p.price;
      div.dataset.comparePriceCents = p.comparePriceCents || 0;
      div.dataset.comparePrice = p.comparePrice || '';
      div.dataset.collection = `extras-${handle}`;
      div.dataset.img = p.img || '';
      div.dataset.available = isAvailable; // Vital for JS logic
      div.setAttribute('role', 'listitem');
      div.style.display = 'grid';

      const key = `extras-${handle}-${p.variantId}`;
      const isSelected = this._state.has(key);

      div.innerHTML = `
        ${p.img ? `<img class="bb__product-img" src="${_esc(p.img)}" alt="${_esc(p.title)}" width="90" height="90" loading="lazy">` : ''}
        ${!isAvailable ? '<span class="bb__sold-out-badge">Sold Out</span>' : ''}
        <div class="bb__product-body">
          <p class="bb__product-title">${_esc(p.title)}</p>
          <p class="bb__product-sku">SKU: ${_esc(p.sku)}</p>
          <div class="bb__product-pricing">
            <p class="bb__product-price">${_esc(p.price)}</p>
            ${p.comparePriceCents > p.priceCents ? `<p class="bb__product-compare-price">${_esc(p.comparePrice)}</p>` : ''}
          </div>
          ${_warrantyHTML(p.variantId, p.priceCents)}
          <div class="card-btns">
            <button class="bb__product-add" type="button" ${!isAvailable ? 'disabled' : ''}>
              ${!isAvailable ? 'Sold Out' : (isSelected ? 'Remove' : 'Add')}
            </button>
            <button class="bb__view-details button" type="button">View Details</button>
          </div>
        </div>`;

      if (isSelected) {
        div.classList.add('bb__product--selected');
        const saved = this._state.get(key);
        const radio = div.querySelector(`input[name="warranty-${p.variantId}"][value="${saved?.warranty || 'none'}"]`);
        if (radio) radio.checked = true;
      }

      this._productsEl.appendChild(div);
    });

    this._bodyEl.removeAttribute('hidden');
    if (this._placeholder) this._placeholder.hidden = true;
    if (this._filter) { this._filter.destroy(); }
    this._filter = new PanelFilters(this._filtersEl, this._productsEl);
  }
}

class BundleUI {
  constructor(root) {
    this._root = root;
    this._openKey = null;
    this._panelFilters = new Map();
  }
  openPanel(collection) {
    if (this._openKey && this._openKey !== collection) {
      this._closePanelSilent(this._openKey);
    }
    const acc = this._accordion(collection);
    const panel = this._panel(collection);
    if (!acc || !panel) return;

    acc.classList.add('bb__accordion--open');
    acc.querySelector('.bb__toggle').setAttribute('aria-expanded', 'true');
    panel.removeAttribute('hidden');
    this._highlightVisual(collection, true);
    this._openKey = collection;

    if (!this._panelFilters.has(collection)) {
      const productsContainer = panel.querySelector('.bb__products');
      this._panelFilters.set(collection, new PanelFilters(panel, productsContainer));
    } else {
      const filterInstance = this._panelFilters.get(collection);
      if (filterInstance) {
        requestAnimationFrame(() => { filterInstance.showAll(); });
      }
    }
  }
  closeAllPanels() {
    this._root.querySelectorAll('.bb__accordion--open').forEach(acc => {
      this._closePanelSilent(acc.dataset.collection);
    });
    this._openKey = null;
  }
  togglePanel(collection) {
    if (this._openKey === collection) {
      this._closePanelSilent(collection);
      this._openKey = null;
    } else {
      this.openPanel(collection);
    }
  }
  _closePanelSilent(collection) {
    const acc = this._accordion(collection);
    const panel = this._panel(collection);
    if (acc) acc.classList.remove('bb__accordion--open');
    if (acc) acc.querySelector('.bb__toggle')?.setAttribute('aria-expanded', 'false');
    if (panel) panel.setAttribute('hidden', '');
    this._highlightVisual(collection, false);
    this._panelFilters.get(collection)?.reset();
  }
  _highlightVisual(collection, active) {
    this._visual(collection)?.classList.toggle('bb__visual--active', active);
  }
  setVisualProduct(collection, src, alt) {
    const v = this._visual(collection);
    if (!v) return;
    const img = v.querySelector('.bb__visual-product');
    if (img) { img.src = src || ''; img.alt = alt || ''; }
    v.classList.add('bb__visual--has-product');
  }
  clearVisual(collection) {
    const v = this._visual(collection);
    if (!v) return;
    const img = v.querySelector('.bb__visual-product');
    if (img) { img.src = ''; img.alt = ''; }
    v.classList.remove('bb__visual--has-product');
  }
  markProductSelected(el, selected) {
    el.classList.toggle('bb__product--selected', selected);
    const btn = el.querySelector('.bb__product-add');
    if (!btn) return;
    const isSoldOut = el.classList.contains('bb__product--sold-out');
    if (isSoldOut) return; // Don't change text if sold out
    
    btn.textContent = selected ? 'Remove' : 'Add';
  }
  disableSiblingsInPanel(collection) {
    this._panel(collection)?.querySelectorAll('.bb__product').forEach(p => {
      if (p.classList.contains('bb__product--selected') || p.classList.contains('bb__product--sold-out')) return;
      p.classList.add('bb__product--disabled');
      const btn = p.querySelector('.bb__product-add');
      if (btn) { btn.disabled = true; btn.setAttribute('aria-disabled', 'true'); }
    });
  }
  enableAllInPanel(collection) {
    this._panel(collection)?.querySelectorAll('.bb__product').forEach(p => {
      if (p.classList.contains('bb__product--sold-out')) return;
      
      p.classList.remove('bb__product--disabled');
      const btn = p.querySelector('.bb__product-add');
      if (btn) { btn.disabled = false; btn.setAttribute('aria-disabled', 'false'); }
    });
  }
  clearSelectionInPanel(collection) {
    this._panel(collection)?.querySelectorAll('.bb__product--selected').forEach(p => {
      this.markProductSelected(p, false);
    });
    const lbl = this._accordion(collection)?.querySelector('.bb__toggle-selected');
    if (lbl) lbl.textContent = '';
  }
  setAccordionLabel(collection, html) {
    const el = this._accordion(collection)?.querySelector('.bb__toggle-selected');
    if (el) el.innerHTML = html || '';
  }
  showActions() { this._root.querySelector('#bb-actions')?.removeAttribute('hidden'); }
  hideActions() { this._root.querySelector('#bb-actions')?.setAttribute('hidden', ''); }
  resetAll() {
    this.closeAllPanels();
    this._root.querySelectorAll('.bb__accordion').forEach(acc => {
      const collection = acc.dataset.collection;
      this.clearSelectionInPanel(collection);
      this.clearVisual(collection);
      this.setAccordionLabel(collection, '');
      this.enableAllInPanel(collection);
    });
    this.hideActions();
    updateSteps(1);
  }
  _accordion(c) { return this._root.querySelector(`.bb__accordion[data-collection="${CSS.escape(c)}"]`); }
  _panel(c) { return this._accordion(c)?.querySelector('.bb__panel') || null; }
  _visual(c) { return this._root.querySelector(`.bb__visual[data-collection="${CSS.escape(c)}"]`); }
}

class BundleBuilder {

  updateBundleButton() {
    const selectedProducts = this._state.size;
    const btn = this._root.querySelector('#bb-add-cart-btn');
    if (!btn) return;

    if (selectedProducts < 2) {
      btn.disabled = true;
      btn.classList.add('is-disabled');
      btn.textContent = 'Select at least 2 Products';
    } else {
      btn.disabled = false;
      btn.classList.remove('is-disabled');
      btn.textContent = 'Add Bundle To Cart';
    }
  }

  async addBundleToCart() {
    const btn = this._root.querySelector('#bb-add-cart-btn');
    if (!btn || btn.dataset.processing === 'true') return;

    const selectedItems = this._state.getAll();
    if (selectedItems.length < 2) {
      alert('Please select at least 2 products.');
      return;
    }

    const rootPath = window.Shopify?.routes?.root || '/';
    const bundleId = `TKB-${Date.now()}`;
    const itemsToAdd = [];
    const errors = [];

    for (const item of selectedItems) {
      const productVariantId = Number(item.variantId);
      if (!Number.isSafeInteger(productVariantId) || productVariantId <= 0) {
        errors.push(`Invalid product variant for ${item.title || 'a selected product'}.`);
        continue;
      }

      // The product must still be present and explicitly available in the rendered builder.
      const productEl = this._root.querySelector(
        `.bb__product[data-variant-id="${CSS.escape(String(item.variantId))}"]`
      );
      const available = productEl
        ? productEl.dataset.available !== 'false' && productEl.dataset.available !== '0'
        : false;

      if (!available) {
        errors.push(`${item.title || 'A selected product'} is no longer available.`);
        continue;
      }

      itemsToAdd.push({
        id: productVariantId,
        quantity: 1,
        properties: {
          '_bundle_id': bundleId,
          '_bundle_name': 'Thor Kitchen Bundle',
          ...(item.warranty && ['2', '3', '5'].includes(String(item.warranty))
            ? { 'Warranty term': `${item.warranty} years` }
            : {})
        }
      });

      const term = String(item.warranty || 'none');
      if (!['2', '3', '5'].includes(term)) continue;

      const warranty = getCpsWarrantyVariant(item.priceCents, term);
      if (!warranty || !Number.isSafeInteger(Number(warranty.variantId))) {
        errors.push(
          `Warranty mapping not found for ${item.title || 'selected product'} ` +
          `($${(Number(item.priceCents) / 100).toFixed(2)}, ${term}-year warranty).`
        );
        continue;
      }

      // Add the actual CPS warranty variant as a separate Shopify cart line.
      itemsToAdd.push({
        id: Number(warranty.variantId),
        quantity: 1,
        properties: {
          '_bundle_id': bundleId,
          '_bundle_name': 'Thor Kitchen Bundle',
          'Warranty term': `${term} years`,
          'Covered appliance': item.title || '',
          'Appliance SKU': item.sku || ''
        }
      });
    }

    if (errors.length) {
      console.error('[BundleBuilder] Bundle validation failed:', errors);
      alert(errors.join('\n'));
      return;
    }

    btn.dataset.processing = 'true';
    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = 'Adding bundle...';

    try {
      const response = await fetch(`${rootPath}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items: itemsToAdd })
      });

      if (!response.ok) {
        let message = 'Shopify could not add the selected bundle to the cart.';
        try {
          const errorData = await response.json();
          if (errorData.description) message = errorData.description;
          else if (errorData.message) message = errorData.message;
        } catch (_) {}
        throw new Error(message);
      }

      await response.json();

      // Use Shopify's localized root path for cart navigation.
      window.location.assign(`${rootPath}checkout`);
    } catch (error) {
      console.error('[BundleBuilder] Add bundle to cart failed:', error);
      alert(`${error.message || 'Unable to add the bundle to cart.'} Please try again.`);
      btn.disabled = false;
      btn.dataset.processing = 'false';
      btn.textContent = originalText;
    }
  }

  constructor(root) {
    this._root = root;
    this._state = new BundleState();
    this._ui = new BundleUI(root);

    const extrasScript = document.getElementById('bb-extras-data');
    let extrasData = {};
    if (extrasScript) {
      try { extrasData = JSON.parse(extrasScript.textContent); } 
      catch (e) { console.error('[BundleBuilder] Extras JSON parse error:', e); }
    }

    const extrasPanel = root.querySelector('.bb__panel--extras');
    if (extrasPanel) {
      this._extras = new BundleExtras(extrasPanel, this._state, this._ui, extrasData);
    }

    this._initAvailability(); // Initialize availability state
    this._wire();
    this.updateBundleButton();

    this._state.onChange(s => {
      if (s.size > 0) {
        this._ui.showActions();
      } else {
        this._ui.resetAll();
      }
      this.updateBundleButton();
      this._updateTotal();
      this._updateExtrasCount();
      this._syncLeftCategories();
    });
  }

  _initAvailability() {
    // Backward-compatible: only an explicit false/0 marks a product unavailable.
    this._root.querySelectorAll('.bb__product').forEach(el => {
      if (el.dataset.available === undefined || el.dataset.available === '') {
        el.dataset.available = 'true';
      }

      const available = el.dataset.available !== 'false' && el.dataset.available !== '0';
      if (!available) {
        el.classList.add('bb__product--sold-out');
        el.setAttribute('aria-disabled', 'true');
        const btn = el.querySelector('.bb__product-add');
        if (btn) {
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
          btn.textContent = 'Sold Out';
        }
        el.querySelectorAll('input[name^="warranty-"]').forEach(input => {
          input.disabled = true;
        });
      }
    });
  }

  _syncLeftCategories() {
    const selected = new Set();
    this._state.getAll().forEach(item => {
      selected.add(item.collection);
      if (item.collection && item.collection.startsWith('extras-')) {
        selected.add('extras');
      }
    });
    this._root.querySelectorAll('.bb__category-card').forEach(card => {
      const collection = card.dataset.collection;
      const checkbox = card.querySelector('.bb__category-check');
      if (selected.has(collection)) {
        card.classList.add('active');
        if (checkbox) checkbox.checked = true;
      } else {
        card.classList.remove('active');
        if (checkbox) checkbox.checked = false;
      }
    });
  }

  _wire() {
    const root = this._root;

    // Toggles
    root.querySelectorAll('.bb__toggle').forEach(btn => {
      btn.addEventListener('click', e => {
        if (e.target.closest('.bb__card-close')) return;
        e.stopPropagation();
        const col = btn.closest('.bb__accordion').dataset.collection;
        this._ui.togglePanel(col);
      });
    });

    // Close Panel
    root.addEventListener('click', e => {
      if (e.target.closest('.bb__panel-close')) {
        e.stopPropagation();
        this._ui.closeAllPanels();
      }
    });

    // Outside Click
    document.addEventListener('click', e => {
      if (!e.target.closest('.bb__panel') && !e.target.closest('.bb__toggle') && !e.target.closest('.bb__visual') && !e.target.closest('.bb__category-card')) {
        this._ui.closeAllPanels();
      }
    });

    // Visual Clicks
    root.querySelectorAll('.bb__visual').forEach(v => {
      v.addEventListener('click', e => {
        if (e.target.closest('.bb__visual-remove')) return;
        e.stopPropagation();
        this._ui.openPanel(v.dataset.collection);
      });
      v.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (!e.target.closest('.bb__visual-remove')) this._ui.openPanel(v.dataset.collection);
        }
      });
    });

    // Remove from Visual
    root.addEventListener('click', e => {
      const rb = e.target.closest('.bb__visual-remove');
      if (!rb) return;
      e.stopPropagation();
      this._removeItem(rb.closest('.bb__visual').dataset.collection);
    });

    // Category Cards
    root.addEventListener('click', e => {
      const card = e.target.closest('.bb__category-card');
      if (!card) return;
      if (e.target.closest('.bb__category-checkbox')) return;
      const collection = card.dataset.collection;
      if (!collection) return;
      this._ui.openPanel(collection);
      if (window.innerWidth <= 768) {
        const configSection = document.getElementById('bb-config');
        if (configSection) {
          setTimeout(() => { configSection.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 200);
        }
      }
    });

    // ADD TO BUNDLE BUTTON CLICK
    root.addEventListener('click', e => {
      const addBtn = e.target.closest('.bb__product-add');
      if (!addBtn) return;

      const productEl = addBtn.closest('.bb__product');
      if (!productEl) return;

      // CHECK AVAILABILITY
      const isAvailable = productEl.dataset.available === 'true';
      if (!isAvailable) {
        console.warn('Attempted to add sold out product');
        return;
      }

      if (productEl.classList.contains('bb__product--disabled')) return;

      const collection = productEl.dataset.collection;
      const variantId = productEl.dataset.variantId;
      const isExtra = collection?.startsWith('extras-');

      const radio = productEl.querySelector(`input[name="warranty-${variantId}"]:checked`);
      const warranty = radio ? radio.value : 'none';

      const data = {
        variantId: parseInt(variantId, 10),
        title: productEl.dataset.title || '',
        sku: productEl.dataset.sku || '',
        priceCents: (() => {
          const cents = Number.parseInt(productEl.dataset.priceCents, 10);
          if (Number.isFinite(cents) && cents >= 0) return cents;
          const priceText = productEl.dataset.price || '';
          const dollars = Number.parseFloat(priceText.replace(/[^0-9.]/g, ''));
          return Number.isFinite(dollars) ? Math.round(dollars * 100) : 0;
        })(),
        comparePriceCents: parseInt(productEl.dataset.comparePriceCents, 10) || 0,
        img: productEl.dataset.img || '',
        warranty,
        collection,
      };

      if (isExtra) {
        const key = `extras-${collection.replace(/^extras-/, '')}-${variantId}`;
        const allExtrasProducts = this._root.querySelectorAll(`.bb__product[data-collection="${collection}"]`);

        if (this._state.has(key)) {
          this._state.remove(key);
          this._ui.markProductSelected(productEl, false);
          const noneRadio = productEl.querySelector(`input[name="warranty-${variantId}"][value="none"]`);
          if (noneRadio) noneRadio.checked = true;

          allExtrasProducts.forEach(p => {
            if(p.dataset.available === 'true') {
                p.classList.remove('bb__product--disabled');
                const btn = p.querySelector('.bb__product-add');
                if (btn) { btn.disabled = false; btn.setAttribute('aria-disabled', 'false'); }
            }
          });
        } else {
          this._state.add(key, data);
          this._ui.markProductSelected(productEl, true);
          allExtrasProducts.forEach(p => {
            if (p === productEl || p.dataset.available === 'false') return;
            p.classList.add('bb__product--disabled');
            const btn = p.querySelector('.bb__product-add');
            if (btn) { btn.disabled = true; btn.setAttribute('aria-disabled', 'true'); }
          });
        }
      } else {
        // Primary Product Logic
        if (this._state.has(collection)) {
          this._removeItem(collection);
        } else {
          this._addItem(collection, productEl, data);
        }
      }
      this._ui.closeAllPanels();
    });

    root.querySelector('#bb-add-cart-btn')?.addEventListener('click', () => {
      this.addBundleToCart();
    });

    // View Details Logic
    this._root.addEventListener('click', e => {
      const btn = e.target.closest('.bb__view-details-selected');
      if (!btn) return;
      const variantId = btn.dataset.variant;
      const productEl = this._root.querySelector(`.bb__product[data-variant-id="${variantId}"]`);
      if (productEl) {
        productEl.querySelector('.bb__view-details')?.click();
      }
    });

    // Remove Item Logic
    this._root.addEventListener('click', e => {
      const removeBtn = e.target.closest('.bb__selected-remove, .bb__card-close');
      if (!removeBtn) return;
      const collection = removeBtn.dataset.remove;
      this._removeItem(collection);
    });
  }

  _addItem(collection, productEl, data) {
    this._ui.clearSelectionInPanel(collection);
    this._ui.markProductSelected(productEl, true);
    const radio = productEl.querySelector(`input[name="warranty-${data.variantId}"][value="${data.warranty}"]`);
    if (radio) radio.checked = true;

    this._state.add(collection, data);
    this._ui.setVisualProduct(collection, data.img, data.title);

    const acc = this._root.querySelector(`.bb__accordion[data-collection="${collection}"]`);
    acc?.classList.add('bb__accordion--has-selection');

    const warrantyLabel =
      data.warranty === '2' ? '2 Year Warranty' :
      data.warranty === '3' ? '3 Year Warranty' :
      data.warranty === '5' ? '5 Year Warranty' : 'No Warranty';

    this._ui.setAccordionLabel(collection, `
      <div class="bb__selected-card">
        <button class="bb__card-close" data-remove="${collection}">✕</button>
        <img src="${_esc(data.img || '')}" alt="${_esc(data.title || '')}" class="bb__selected-img" />
        <div class="bb__selected-info">
          <p class="bb__selected-title">${_esc(data.title)}</p>
          <p class="bb__selected-sku">SKU: ${_esc(data.sku)}</p>
          <div class="bb__selected-pricing">
            ${data.comparePriceCents > data.priceCents ? `<p class="bb__selected-compare-price">${this._formatMoney(data.comparePriceCents)}</p>` : ''}
            <p class="bb__selected-price">${this._formatMoney(data.priceCents)}</p>
          </div>
          <p class="bb__selected-warranty">Warranty: ${warrantyLabel}</p>
        </div>
      </div>
    `);

    this._ui.disableSiblingsInPanel(collection);
    if (this._state.size >= 1) {
      updateSteps(2);
    }
  }

  _removeItem(collection) {
    this._ui.enableAllInPanel(collection);
    const panel = this._root.querySelector(`.bb__accordion[data-collection="${CSS.escape(collection)}"] .bb__panel`);
    panel?.querySelectorAll('.bb__product--selected').forEach(p => {
      this._ui.markProductSelected(p, false);
      const vid = p.dataset.variantId;
      const radio = p.querySelector(`input[name="warranty-${vid}"][value="none"]`);
      if (radio) radio.checked = true;
    });

    this._ui.clearSelectionInPanel(collection);
    this._ui.clearVisual(collection);
    this._ui.setAccordionLabel(collection, '');
    this._state.remove(collection);

    if (this._state.size === 0) {
      updateSteps(1);
    }

    const acc = this._root.querySelector(`.bb__accordion[data-collection="${collection}"]`);
    acc?.classList.remove('bb__accordion--has-selection');
  }

  _updateTotal() {
    let total = 0;
    this._state.getAll().forEach(item => {
      let priceCents = item.priceCents || 0;
      const warrantyPrice = getWarrantyPrice(item);
      priceCents += Math.round(warrantyPrice * 100);
      total += priceCents;
    });
    this._total = total;
    const newEl = this._root.querySelector('#bb-total-new');
    if (newEl) newEl.textContent = this._formatMoney(total);
  }

  _formatMoney(cents) { return '$' + (cents / 100).toFixed(2); }

  _getExtrasCount() {
    let count = 0;
    this._state.getAll().forEach(item => {
      if (item.collection && item.collection.startsWith('extras-')) count++;
    });
    return count;
  }

  _updateExtrasCount() {
    const count = this._getExtrasCount();
    const extrasAcc = this._root.querySelector(`.bb__accordion[data-collection="extras"]`);
    if (!extrasAcc) return;
    const label = extrasAcc.querySelector('.bb__toggle-selected');
    if (!label) return;
    if (count > 0) {
      label.innerHTML = `<span class="bb__extras-count">(${count}) items selected</span>`;
    } else {
      label.innerHTML = '';
    }
  }
}

function initExtrasFilterButtons() {
  document.querySelectorAll('.bb__extras-filters .bb__filter-btn').forEach(btn => {
    btn.removeEventListener('click', handleExtrasFilterClick);
    btn.addEventListener('click', handleExtrasFilterClick);
  });
}

function handleExtrasFilterClick(e) {
  const btn = e.target.closest('.bb__filter-btn');
  if (!btn) return;
  const filterType = btn.dataset.filter;
  const value = btn.dataset.value.toLowerCase();
  const isActive = btn.getAttribute('aria-pressed') === 'true';

  btn.closest('.bb__filter-group').querySelectorAll('.bb__filter-btn').forEach(b => {
    b.setAttribute('aria-pressed', 'false');
    b.classList.remove('is-active');
  });

  if (!isActive) {
    btn.setAttribute('aria-pressed', 'true');
    btn.classList.add('is-active');
  }
  filterExtrasProducts();
}

function filterExtrasProducts() {
  const filters = {};
  document.querySelectorAll('.bb__extras-filters .bb__filter-btn.is-active').forEach(btn => {
    const filterType = btn.dataset.filter;
    const value = btn.dataset.value.toLowerCase().trim();
    if (!filters[filterType]) filters[filterType] = [];
    filters[filterType].push(value);
  });

  let visibleCount = 0;
  document.querySelectorAll('.bb__extras-products .bb__product').forEach(p => {
    let show = true;
    for (const [filterType, values] of Object.entries(filters)) {
      const productVal = (p.dataset[filterType] || '').toLowerCase().trim();
      if (!values.includes(productVal)) { show = false; break; }
    }
    p.style.display = show ? 'grid' : 'none';
    if (show) visibleCount++;
  });

  const container = document.querySelector('.bb__extras-products');
  const msgEl = container?.querySelector('.bb__no-results');
  if (msgEl) { msgEl.style.display = visibleCount === 0 ? 'block' : 'none'; }
}

function initWarrantyUI() {
  document.querySelectorAll('.bb__product-warranty').forEach(wrap => {
    const priceAttr = wrap.dataset.price;
    if (!priceAttr) return;
    const price = parseFloat(priceAttr.replace(/,/g, ''));
    const priceCents = Math.round(price * 100);
    const warranties = getWarrantyPrices(priceCents);

    wrap.querySelectorAll('input[type="radio"]').forEach(input => {
      if (input.value === 'none') return;
      const years = input.value;
      const label = input.closest('label');
      const warrantyPrice = warranties[years];
      if (warrantyPrice) {
        label.innerHTML = `<input type="radio" name="${input.name}" value="${years}"> ${years} Year (+$${warrantyPrice.toFixed(2)})`;
      }
    });
  });
}

function initBundleBuilder() {
  document.querySelectorAll('[id^="bundle-builder-"]').forEach(root => {
    if (root.dataset.bbInit) return;
    root.dataset.bbInit = '1';
    try { root.__bundleBuilder = new BundleBuilder(root); } 
    catch (err) { console.error('[BundleBuilder] Init error:', err); }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initBundleBuilder();
    initWarrantyUI();
  });
} else {
  initBundleBuilder();
  initWarrantyUI();
}

document.addEventListener('shopify:section:load', initBundleBuilder);

// View details popup logic
document.addEventListener('click', function (e) {
  const btn = e.target.closest('.bb__view-details');
  if (btn) {
    e.stopPropagation();
    const product = btn.closest('.bb__product');
    const modal = document.getElementById('bb-quickview');
    if (!product || !modal) return;
    modal.currentProduct = product;

    const qvWarrantyWrap = modal.querySelector('.qv-warranty');
    if (qvWarrantyWrap) {
      const priceCents = parseInt(product.dataset.priceCents, 10) || 0;
      const warranties = getWarrantyPrices(priceCents);
      const available = product.dataset.available !== 'false' && product.dataset.available !== '0';
      const disabled = available ? '' : ' disabled';
      let html = `<h4>Extended Warranty</h4><label><input type="radio" name="qv-warranty" value="none" checked${disabled}> No Warranty</label>`;
      Object.entries(warranties).forEach(([years, price]) => {
        html += `<label><input type="radio" name="qv-warranty" value="${years}"${disabled}> ${years} Year (+$${price.toFixed(2)})</label>`;
      });
      qvWarrantyWrap.innerHTML = html;
    }

    const img = modal.querySelector('#qv-image');
    const title = modal.querySelector('#qv-title');
    const sku = modal.querySelector('#qv-sku');
    const desc = modal.querySelector('#qv-description');
    const prevBtn = modal.querySelector('.prev');
    const nextBtn = modal.querySelector('.next');
    const thumbsContainer = modal.querySelector('.qv-thumbs');
    const gallery = modal.querySelector('.bb__quickview-gallery');

    if (title) title.textContent = product.dataset.title || '';
    if (sku) sku.textContent = 'SKU: ' + (product.dataset.sku || '');
    if (desc) {
      desc.innerHTML = product.dataset.description || product.querySelector('.bb__product-description')?.innerHTML || '<p>No description available</p>';
    }

    document.querySelectorAll('input[name="qv-warranty"]').forEach(r => { r.checked = r.value === 'none'; });

    function renderCleanHTML(html, fallback) {
      if (!html) return fallback;
      const txt = document.createElement("textarea");
      txt.innerHTML = html;
      let decoded = txt.value;
      const temp = document.createElement("div");
      temp.innerHTML = decoded;
      temp.querySelectorAll('.container, .tables-row, .table-col, .tk-custom-tab').forEach(el => el.replaceWith(...el.childNodes));
      temp.querySelectorAll('br').forEach(br => br.remove());
      temp.querySelectorAll('*').forEach(el => {
        const isImage = el.tagName === 'IMG' || el.tagName === 'PICTURE';
        if (!isImage && !el.textContent.trim() && el.children.length === 0) el.remove();
      });
      return temp.innerHTML.trim() || fallback;
    }

    const techEl = modal.querySelector('#qv-tech');
    const dimEl = modal.querySelector('#qv-dim');
    const cutEl = modal.querySelector('#qv-cut');
    const pdfContainer = modal.querySelector('#qv-spec');

    if (techEl) techEl.innerHTML = renderCleanHTML(product.dataset.tech, '<p>No technical data</p>');
    if (dimEl) dimEl.innerHTML = renderCleanHTML(product.dataset.dim, '<p>No dimensions available</p>');
    if (cutEl) cutEl.innerHTML = renderCleanHTML(product.dataset.cut, '<p>No cutout available</p>');
    if (pdfContainer) pdfContainer.innerHTML = product.dataset.pdf || '<p>No manual available</p>';

    modal.querySelectorAll('.qv-tab').forEach(t => t.classList.remove('active'));
    modal.querySelectorAll('.qv-tab-content').forEach(c => c.classList.remove('active'));
    modal.querySelector('[data-tab="tech"]')?.classList.add('active');
    modal.querySelector('#qv-tech')?.classList.add('active');

    const tabs = modal.querySelectorAll('.qv-tab');
    const contents = modal.querySelectorAll('.qv-tab-content');
    tabs.forEach(tab => {
      tab.onclick = () => {
        tabs.forEach(t => t.classList.remove('active'));
        contents.forEach(c => c.classList.remove('active'));
        tab.classList.add('active');
        modal.querySelector('#qv-' + tab.dataset.tab)?.classList.add('active');
      };
    });

    let images = [];
    try {
      const parsed = JSON.parse(product.dataset.images || '[]');
      images = parsed.map(img => img.src || img);
    } catch (e) { console.warn('Invalid images JSON', e); }
    if (!images.length && product.dataset.img) images = [product.dataset.img];
    if (!images.length) return;

    let index = 0;
    if (prevBtn) prevBtn.hidden = images.length <= 1;
    if (nextBtn) nextBtn.hidden = images.length <= 1;
    if (thumbsContainer) thumbsContainer.innerHTML = '';

    function updateImage() {
      if (!img) return;
      gallery?.classList.add('loading');
      const temp = new Image();
      temp.src = images[index];
      temp.onload = () => {
        img.src = temp.src;
        gallery?.classList.remove('loading');
      };
      thumbsContainer?.querySelectorAll('img').forEach((t, i) => { t.classList.toggle('active', i === index); });
    }

    images.forEach((src, i) => {
      const t = document.createElement('img');
      t.src = src;
      t.onclick = () => { index = i; updateImage(); };
      thumbsContainer?.appendChild(t);
    });

    if (prevBtn) { prevBtn.onclick = () => { index = (index - 1 + images.length) % images.length; updateImage(); }; }
    if (nextBtn) { nextBtn.onclick = () => { index = (index + 1) % images.length; updateImage(); }; }

    updateImage();
    modal.removeAttribute('hidden');
  }

  if (e.target.closest('#qv-add-btn')) {
    const modal = document.getElementById('bb-quickview');
    const product = modal?.currentProduct;
    if (!product) return;

    const originalAddBtn = product.querySelector('.bb__product-add');
    const selectedWarranty = document.querySelector('input[name="qv-warranty"]:checked')?.value || 'none';
    const variantId = product.dataset.variantId;

    const originalRadio = product.querySelector(`input[name="warranty-${variantId}"][value="${selectedWarranty}"]`);
    if (originalRadio) originalRadio.checked = true;
    if (originalAddBtn) originalAddBtn.click();

    modal.setAttribute('hidden', '');
  }

  if (e.target.classList.contains('bb__quickview-overlay') || e.target.classList.contains('bb__quickview-close')) {
    document.getElementById('bb-quickview')?.setAttribute('hidden', '');
  }
});

document.addEventListener('DOMContentLoaded', () => {
  const btn = document.getElementById('bb-start-btn');
  const hero = document.getElementById('bb-hero');
  const builder = document.querySelector('.bb');

  if (btn) {
    btn.addEventListener('click', () => {
      hero.style.display = 'none';
      builder.style.display = 'grid';
      window.scrollTo({ top: 0, behavior: 'smooth' });
      updateSteps(1);
    });
  }
});

function updateSteps(step) {
  const items = document.querySelectorAll('.bb-step-item');
  items.forEach((item, index) => {
    item.classList.remove('active');
    if (index < step) item.classList.add('active');
  });
  document.querySelectorAll('.step-label').forEach((el, i) => {
    el.classList.toggle('active', i < step);
  });
}